// src/components/Center Admin/layout/CenterAppointmentsQueue.tsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/** ---------- Types (UI + API mapping) ---------- */
export type SidebarItem = {
  id: string;
  date: string;
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
  time?: string;
  emrId?: string;
  queueStatus?: "WAITING" | "IN_PROGRESS";
  sessionId?: string;
};

type AppointmentChild = {
  emrId?: string;
  name?: string;
  gender?: string;
  age?: number;
};

type AppointmentRow = {
  _id: string;
  child?: AppointmentChild;
  createdAt?: string;
  appointmentTime?: string;
  checkinTime?: string;
  slotTime?: string;
};

type AppointmentsResp =
  | { items?: AppointmentRow[] }
  | AppointmentRow[]
  | { count?: number; bookings?: any[] };

type Props = {
  centerId: string;
  dateISO?: string;
  activeId?: string;
  onSelect?: (id: string, patient?: SidebarItem) => void;
  autoRefreshMs?: number;
  queueApi?: string;
};

/** ---------- Helpers ---------- */
function toTodayISO() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}
function formatDmy(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}
function formatHm(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
function bestTimestamp(row: AppointmentRow): string | undefined {
  return (
    row.appointmentTime ||
    row.checkinTime ||
    row.slotTime ||
    row.createdAt ||
    undefined
  );
}

/** ---------- Component ---------- */
const CenterAppointmentsQueue: React.FC<Props> = ({
  centerId,
  dateISO,
  activeId,
  onSelect,
  autoRefreshMs,
  queueApi,
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<SidebarItem[]>([]);
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({}); // ✅ FIX

  const effectiveDate = dateISO || toTodayISO();

  /* ---------------- PRESIGNED IMAGE FETCH (FIXED) ---------------- */
  useEffect(() => {
    const fetchImages = async () => {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      const map: Record<string, string> = {};

      for (const item of items) {
        // 1. Get path string
        const filePath = item.avatarUrl;

        // Skip if empty
        if (!filePath) continue;

        try {
          // 2. Remove 'new URL()'. Split manually.
          const parts = filePath.split("/");

          if (parts.length < 3) {
            console.warn("Invalid file path format:", filePath);
            continue;
          }

          // 3. Extract Module
          const module = `${parts[0]}/${parts[1]}`;

          // 4. Extract Key
          const key = parts.slice(2).join("/");

          // 5. Call API
          const res = await api.S3config.get("/view-url", {
            params: { module, key },
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.data?.url) {
            map[item.id] = res.data.url;
          }
        } catch (err) {
          console.error(`Image view failed for ${item.name}`, err);
        }
      }

      setAvatarMap(map);
    };

    if (items.length) fetchImages();
  }, [items]);
  /* -------------------------------------------------------------- */

  // 🔹 Fetch gender and DOB dynamically from Parent-Child microservice
  async function fetchChildDetails(
    emrId: string,
    token: string,
  ): Promise<{ genderShort: "M" | "F"; ageYears: number }> {
    try {
      const { data } = await api.parentchildApi.get(
        `/children/emr/${encodeURIComponent(emrId)}/details`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const genderRaw = data?.data?.childGender || "";
      const dobRaw = data?.data?.childDob || null;

      // 🔹 Calculate age in years
      let ageYears = 0;
      if (dobRaw) {
        const dob = new Date(dobRaw);
        const now = new Date();
        ageYears = now.getFullYear() - dob.getFullYear();
        const m = now.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) ageYears--;
      }

      const g = String(genderRaw).toUpperCase();
      return {
        genderShort: g.startsWith("F") ? "F" : "M",
        ageYears: ageYears > 0 ? ageYears : 0,
      };
    } catch (e) {
      console.warn("Parent-Child fetch failed for:", emrId, e);
      return { genderShort: "M", ageYears: 0 };
    }
  }

  async function load() {
    if (!centerId) {
      setItems([]);
      setErr("Missing centerId");
      return;
    }

    setLoading(true);
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");
      const headers = { Authorization: `Bearer ${token}` };

      let rows: AppointmentRow[] = [];

      if (queueApi) {
        // ✅ NEW: USE QUEUE API
        const { data } = await api.centerworkflowApi.get(
          `/queues/${queueApi}?center_id=${encodeURIComponent(
            centerId,
          )}&date=${encodeURIComponent(effectiveDate)}`,
          { headers },
        );
        console.log("QUEUE API RESPONSE:", data);

        rows = [
          ...(data?.waiting || []).map((item: any) => ({
            ...item,
            queueStatus: "WAITING",
          })),

          ...(data?.inProgress || []).map((item: any) => ({
            ...item,
            queueStatus: "IN_PROGRESS",
          })),
        ].map((item: any) => {
          console.log("QUEUE ITEM SESSION:", item.sessionId);

          return {
            _id: item._id,

            queueStatus: item.queueStatus, // ✅ NEW

            // ✅ ADD THIS LINE
            sessionId: item.sessionId,

            child: {
              emrId: item.child?.childId,
              name: item.child?.name,
            },

            createdAt: item.createdAt,
            child_profile: item.child?.profile,
          };
        });
      }

      // 🔹 Convert rows → SidebarItem
      const enriched: SidebarItem[] = await Promise.all(
        rows.map(async (r) => {
          const ts = bestTimestamp(r);

          const childInfo = r.child?.emrId
            ? await fetchChildDetails(r.child.emrId, token)
            : { genderShort: "M" as const, ageYears: 0 };

          return {
            id: r._id,
            emrId: r.child?.emrId,
            date: formatDmy(ts),
            time: formatHm(ts),
            name: r.child?.name || "Unknown",
            genderShort: childInfo.genderShort,
            ageYears: childInfo.ageYears,
            avatarUrl: (r as any).child_profile,
            sessionId: (r as any).sessionId,
            queueStatus: (r as any).queueStatus,
          };
        }),
      );

      // 🔹 Sort
      const sorted = enriched.sort((a, b) =>
        (a.time || "").localeCompare(b.time || ""),
      );

      setItems(sorted);

      if (sorted.length === 0) {
        const storageKey = `selectedPatient_${queueApi}`;
        sessionStorage.removeItem(storageKey);

        // window.dispatchEvent(new Event("patientUpdated"));
      }

      // 🔹 Auto-select
      // const hasActive = activeId && sorted.some((m) => m.id === activeId);

      // const firstPatient = sorted[0];

      // if (!hasActive && firstPatient) {
      const storageKey = `selectedPatient_${queueApi}`;

      const stored = sessionStorage.getItem(storageKey);

      const selected = stored ? JSON.parse(stored) : null;

      const hasActive = selected && sorted.some((m) => m.id === selected.id);

      const firstPatient = sorted[0];

      if (!hasActive && firstPatient) {
        const patientWithAvatar = {
          ...firstPatient,
          sessionId: (firstPatient as any).sessionId,
          avatarUrl: avatarMap[firstPatient.id] || undefined,
        };

        const storageKey = `selectedPatient_${queueApi}`;

        sessionStorage.setItem(storageKey, JSON.stringify(patientWithAvatar));

        // ✅ TRIGGER UPDATE
        // window.dispatchEvent(new Event("patientUpdated"));

        // ✅ SET UI
        onSelect?.(firstPatient.id, patientWithAvatar);
      }
    } catch (e: any) {
      setErr(e?.message || "Failed to load appointments");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [centerId, dateISO]);

  useEffect(() => {
    const handlePatientUpdated = () => {
      console.log("PATIENT UPDATED -> Reload Queue");
      load();
    };

    window.addEventListener("patientUpdated", handlePatientUpdated);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdated);
    };
  }, []);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(load, autoRefreshMs);
    return () => clearInterval(t);
  }, [autoRefreshMs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return !q ? items : items.filter((p) => p.name.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <aside className="gc-queue">
      <div className="gc-queue-header">
        <div className="gc-queue-title">Patient Queue</div>
        <div className="gc-queue-searchwrap">
          <input
            className="gc-queue-search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="gc-queue-list">
        {loading && <div className="gc-queue-empty">Loading…</div>}
        {err && !loading && <div className="gc-queue-empty">Error: {err}</div>}
        {!loading && !err && filtered.length === 0 && (
          <div className="gc-queue-empty">No patients</div>
        )}

        {filtered.map((p) => {
          const active = p.id === activeId;
          return (
            <button
              key={p.id}
              className={`
                          gc-queue-item
                          ${active ? "active" : ""}
                          ${
                            p.queueStatus === "WAITING"
                              ? "gc-queue-item-waiting"
                              : "gc-queue-item-progress"
                          }
                        `}
              onClick={() => {
                const patientWithAvatar = {
                  ...p,
                  sessionId: (p as any).sessionId,
                  avatarUrl: avatarMap[p.id] || undefined, // ✅ PRESIGNED URL
                };

                const storageKey = `selectedPatient_${queueApi}`;

                sessionStorage.setItem(
                  storageKey,
                  JSON.stringify(patientWithAvatar),
                );

                // window.dispatchEvent(new Event("patientUpdated"));
                onSelect?.(p.id, patientWithAvatar);
              }}
              title={p.time ? `Time: ${p.time}` : undefined}
            >
              <div className="gc-queue-item-body">
                <div className="gc-queue-avatar">
                  {p.avatarUrl ? (
                    <img
                      src={avatarMap[p.id]}
                      className="sidebar__avatar"
                      alt=""
                    />
                  ) : (
                    <div className="gc-queue-avatar-fallback" />
                  )}
                </div>
                <div className="gc-queue-item-info">
                  <div className="gc-queue-item-date">
                    {p.time ? `${p.time} · ${p.date}` : p.date}
                  </div>
                  <div className="gc-queue-name">{p.name}</div>
                  <div className="gc-queue-sub">
                    {p.genderShort}, {p.ageYears}{" "}
                    {p.ageYears === 1 ? "Year" : "Years"}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

export default CenterAppointmentsQueue;
